import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TrendingUp, Plus, X } from 'lucide-react';
import StockSelector from './StockSelector';
import { useDispatch } from 'react-redux';
import { associateStocksWithGroup } from '../../../redux/userGroups/groupsToStock/groupsToStockActions';
import StockLogo from '../../../components/StockLogo/StockLogoRender';

const GroupStocksSection = ({ 
  groupId, 
  existingStocks = [], 
  isOwner = false,
  onStockAssociationComplete = () => {} 
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [selectedStocks, setSelectedStocks] = useState([]);
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Initialize selected stocks from existing stocks when component mounts or existingStocks change
  useEffect(() => {
    if (Array.isArray(existingStocks) && existingStocks.length > 0) {
      setSelectedStocks(existingStocks);
    } else {
      setSelectedStocks([]);
    }
  }, [existingStocks]);

  const handleSave = async () => {
    if (!selectedStocks || selectedStocks.length === 0) {
      setIsSelectorOpen(false);
      return;
    }

    setIsLoading(true);
    try {
      const stockIds = selectedStocks.map(s => s.id);
      const result = await dispatch(associateStocksWithGroup({ groupId, stockIds }));
      
      if (result.meta.requestStatus === 'fulfilled') {
        setIsSelectorOpen(false);
        // Call the callback to refresh group data
        onStockAssociationComplete();
      } else {
        console.warn('Failed to associate stocks:', result.error);
      }
    } catch (error) {
      console.warn('Error associating stocks:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveStock = (stockToRemove) => {
    const updatedStocks = selectedStocks.filter(s => s.id !== stockToRemove.id);
    setSelectedStocks(updatedStocks);
  };

  const handleCancel = () => {
    // Reset to existing stocks
    setSelectedStocks(existingStocks || []);
    setIsSelectorOpen(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
          <TrendingUp size={18} className="text-indigo-400" />
          <span>{t('groups.stockAssociation.associatedStocks')}</span>
        </h3>
        {isOwner && (
          <button
            onClick={() => setIsSelectorOpen(true)}
            className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl flex items-center space-x-2 transition-all duration-300 disabled:opacity-50"
            disabled={isLoading}
          >
            <Plus size={16} />
            <span>{t('groups.stockAssociation.addStocks')}</span>
          </button>
        )}
      </div>

      {/* Display Current Stocks */}
      <div className="bg-cream-100 dark:bg-gray-800/50 rounded-2xl border border-cream-300 dark:border-gray-700/50 p-6">
        {existingStocks && existingStocks.length > 0 ? (
          <div className="flex flex-wrap gap-3">
            {existingStocks.map(stock => (
              <div
                key={stock.id}
                className="flex items-center gap-2 bg-indigo-500/20 border border-indigo-500/30 rounded-xl px-3 py-2 group animate-tag-in"
              >
                {/* Stock Logo */}
                <StockLogo 
                  symbol={stock.symbol} 
                  name={stock.Name || stock.name} 
                  size="w-6 h-6" 
                />

                <div className="flex flex-col">
                  <span className="text-blue-300 font-medium">{stock.symbol}</span>
                  {(stock.Name || stock.name) && (
                    <span className="text-xs text-gray-600 dark:text-gray-400 truncate max-w-[120px]">
                      {stock.Name || stock.name}
                    </span>
                  )}
                </div>

                {isOwner && (
                  <button
                    onClick={() => handleRemoveStock(stock)}
                    className="text-red-400 hover:text-red-300 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-200"
                    disabled={isLoading}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <TrendingUp className="mx-auto text-gray-400 dark:text-gray-500 mb-4" size={48} />
            <h4 className="text-lg font-semibold text-gray-600 dark:text-gray-400 mb-2">{t('groups.stockAssociation.noStocksYet')}</h4>
            <p className="text-gray-500 dark:text-gray-500 text-sm">
              {isOwner
                ? t('groups.stockAssociation.noStocksOwnerHint')
                : t('groups.stockAssociation.noStocksMemberHint')
              }
            </p>
          </div>
        )}
      </div>

      {/* Stock Selector Modal */}
      {isSelectorOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-4 sm:p-8 w-full max-w-lg max-h-[80vh] min-h-0">
            <div className="flex justify-between items-center mb-6">
              <h4 className="text-xl text-gray-900 dark:text-white font-semibold">{t('groups.stockAssociation.manageGroupStocks')}</h4>
              <button
                onClick={handleCancel}
                className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                disabled={isLoading}
              >
                <X size={24} />
              </button>
            </div>

            <div className="mb-6">
              <StockSelector
                selectedStocks={selectedStocks}
                onStocksChange={setSelectedStocks}
                maxStocks={10}
                placeholder={t('groups.stockAssociation.searchToAssociatePlaceholder')}
                disabled={isLoading}
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 transition-all disabled:opacity-50"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all disabled:opacity-50 flex items-center space-x-2"
                disabled={isLoading}
              >
                {isLoading && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                )}
                <span>{isLoading ? 'Saving...' : 'Save Changes'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupStocksSection;